import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit, Trash2, Copy, Calendar, Users, 
  Package, Sparkles, CheckCircle, XCircle, Settings, Eye
} from 'lucide-react';

import { API_URL } from '../utils/api';

// Available audiences
const AUDIENCES = [
  { id: 'elementary', label: 'Elementary', icon: '🌱' },
  { id: 'middle_school', label: 'Middle School', icon: '🎯' },
  { id: 'high_school', label: 'High School', icon: '🚀' },
  { id: 'adult', label: 'Adult', icon: '🎤' },
  { id: 'sel_coaching', label: 'SEL / Coaching', icon: '💡' },
];

const ProgramManager = () => {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [themes, setThemes] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    program_name: '',
    program_code: '',
    description: '',
    allowed_audiences: ['middle_school'],
    allowed_theme_ids: [],
    allowed_pack_ids: [],
    default_audience: 'middle_school',
    is_active: true,
    start_date: '',
    end_date: ''
  });
  
  // Fetch programs
  const fetchPrograms = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/programs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (err) {
      console.error('Failed to fetch programs:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch themes and packs for selection
  const fetchOptions = useCallback(async () => {
    try {
      const [themesRes, packsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/assessment-themes`, { credentials: 'include' }),
        fetch(`${API_URL}/api/sound-packs`)
      ]);
      
      if (themesRes.ok) {
        const data = await themesRes.json();
        setThemes(data);
      }
      if (packsRes.ok) {
        const data = await packsRes.json();
        setPacks(data.filter(p => p.status === 'published'));
      }
    } catch (err) {
      console.error('Failed to fetch options:', err);
    }
  }, []);
  
  useEffect(() => {
    fetchPrograms();
    fetchOptions();
  }, [fetchPrograms, fetchOptions]);
  
  // Reset form
  const resetForm = () => {
    setFormData({
      program_name: '',
      program_code: '',
      description: '',
      allowed_audiences: ['middle_school'],
      allowed_theme_ids: [],
      allowed_pack_ids: [],
      default_audience: 'middle_school',
      is_active: true,
      start_date: '',
      end_date: ''
    });
    setEditingProgram(null);
  };
  
  // Open create dialog
  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };
  
  // Open edit dialog
  const openEditDialog = (program) => {
    setEditingProgram(program);
    setFormData({
      program_name: program.program_name,
      program_code: program.program_code,
      description: program.description || '',
      allowed_audiences: program.allowed_audiences || ['middle_school'],
      allowed_theme_ids: program.allowed_theme_ids || [],
      allowed_pack_ids: program.allowed_pack_ids || [],
      default_audience: program.default_audience || 'middle_school',
      is_active: program.is_active !== false,
      start_date: program.start_date ? program.start_date.split('T')[0] : '',
      end_date: program.end_date ? program.end_date.split('T')[0] : ''
    });
    setShowDialog(true);
  };
  
  // Save program
  const saveProgram = async () => {
    if (!formData.program_name.trim() || !formData.program_code.trim()) {
      toast.error('Program name and code are required');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      
      const url = editingProgram 
        ? `${API_URL}/api/admin/programs/${editingProgram.program_id}`
        : `${API_URL}/api/admin/programs`;
      
      const response = await fetch(url, {
        method: editingProgram ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success(editingProgram ? 'Program updated' : 'Program created');
        setShowDialog(false);
        resetForm();
        fetchPrograms();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save program');
      }
    } catch (err) {
      toast.error('Error saving program');
    }
  };
  
  // Delete program
  const deleteProgram = async (programId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/programs/${programId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Program deleted');
        setShowDeleteConfirm(null);
        fetchPrograms();
      } else {
        toast.error('Failed to delete program');
      }
    } catch (err) {
      toast.error('Error deleting program');
    }
  };
  
  // Toggle audience in form
  const toggleAudience = (audienceId) => {
    setFormData(prev => {
      const current = prev.allowed_audiences;
      if (current.includes(audienceId)) {
        if (current.length === 1) return prev; // Keep at least one
        return { ...prev, allowed_audiences: current.filter(a => a !== audienceId) };
      }
      return { ...prev, allowed_audiences: [...current, audienceId] };
    });
  };
  
  // Toggle theme in form
  const toggleTheme = (themeId) => {
    setFormData(prev => {
      const current = prev.allowed_theme_ids;
      if (current.includes(themeId)) {
        return { ...prev, allowed_theme_ids: current.filter(t => t !== themeId) };
      }
      return { ...prev, allowed_theme_ids: [...current, themeId] };
    });
  };
  
  // Toggle pack in form
  const togglePack = (packId) => {
    setFormData(prev => {
      const current = prev.allowed_pack_ids;
      if (current.includes(packId)) {
        return { ...prev, allowed_pack_ids: current.filter(p => p !== packId) };
      }
      return { ...prev, allowed_pack_ids: [...current, packId] };
    });
  };
  
  // Copy program code
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Program code copied');
  };
  
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Please log in to access this page</p>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-muted-foreground hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Program Configuration
                </h1>
                <p className="text-sm text-muted-foreground">Manage visibility for schools & programs</p>
              </div>
            </div>
            <Button onClick={openCreateDialog} className="rounded-full bg-primary text-black">
              <Plus className="w-4 h-4 mr-2" />
              New Program
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-20">
            <Settings className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-bold mb-2">No Programs Yet</h2>
            <p className="text-muted-foreground mb-6">
              Create programs to control which audiences, themes, and packs<br />
              are visible to different schools or sessions.
            </p>
            <Button onClick={openCreateDialog} className="rounded-full bg-primary text-black">
              <Plus className="w-4 h-4 mr-2" />
              Create First Program
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {programs.map(program => (
              <div 
                key={program.program_id}
                className={`rounded-2xl border p-5 transition-all ${
                  program.is_active
                    ? 'border-white/10 bg-card/50 hover:border-primary/30'
                    : 'border-white/5 bg-card/20 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{program.program_name}</h3>
                      <button 
                        onClick={() => copyCode(program.program_code)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 text-primary text-sm font-mono hover:bg-primary/20 transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        {program.program_code}
                      </button>
                      <Badge className={program.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                        {program.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    
                    {program.description && (
                      <p className="text-sm text-muted-foreground mb-3">{program.description}</p>
                    )}
                    
                    {/* Audiences */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Audiences:</span>
                        <div className="flex gap-1">
                          {program.allowed_audiences?.map(aud => {
                            const audience = AUDIENCES.find(a => a.id === aud);
                            return (
                              <span key={aud} className="px-2 py-0.5 rounded-full bg-white/10 text-xs">
                                {audience?.icon} {audience?.label || aud}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      
                      {program.allowed_theme_ids?.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">{program.allowed_theme_ids.length} theme(s)</span>
                        </div>
                      )}
                      
                      {program.allowed_pack_ids?.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">{program.allowed_pack_ids.length} pack(s)</span>
                        </div>
                      )}
                      
                      {(program.start_date || program.end_date) && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {program.start_date && new Date(program.start_date).toLocaleDateString()}
                            {program.start_date && program.end_date && ' - '}
                            {program.end_date && new Date(program.end_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(program)}
                      className="rounded-full"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(program)}
                      className="rounded-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              {editingProgram ? 'Edit Program' : 'Create Program'}
            </DialogTitle>
            <DialogDescription>
              Configure visibility settings for this program
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Program Name *</label>
                <Input
                  value={formData.program_name}
                  onChange={e => setFormData(prev => ({ ...prev, program_name: e.target.value }))}
                  placeholder="Summer Music Lab"
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Program Code *</label>
                <Input
                  value={formData.program_code}
                  onChange={e => setFormData(prev => ({ ...prev, program_code: e.target.value.toUpperCase() }))}
                  placeholder="SUMMER26"
                  className="rounded-xl font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">Students enter this to join</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description for admin reference"
                className="rounded-xl"
                rows={2}
              />
            </div>
            
            {/* Audiences */}
            <div>
              <label className="text-sm font-medium mb-2 block">Allowed Audiences</label>
              <p className="text-xs text-muted-foreground mb-3">Students will only see these audience options</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {AUDIENCES.map(aud => (
                  <button
                    key={aud.id}
                    onClick={() => toggleAudience(aud.id)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      formData.allowed_audiences.includes(aud.id)
                        ? 'border-primary bg-primary/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <span className="text-lg mr-2">{aud.icon}</span>
                    <span className="text-sm">{aud.label}</span>
                    {formData.allowed_audiences.includes(aud.id) && (
                      <CheckCircle className="w-4 h-4 text-primary float-right" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Default Audience */}
            <div>
              <label className="text-sm font-medium mb-2 block">Default Audience</label>
              <Select 
                value={formData.default_audience} 
                onValueChange={val => setFormData(prev => ({ ...prev, default_audience: val }))}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {formData.allowed_audiences.map(audId => {
                    const aud = AUDIENCES.find(a => a.id === audId);
                    return (
                      <SelectItem key={audId} value={audId}>
                        {aud?.icon} {aud?.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Themes Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Allowed Themes 
                <span className="text-muted-foreground font-normal ml-2">
                  {formData.allowed_theme_ids.length === 0 ? '(All themes)' : `(${formData.allowed_theme_ids.length} selected)`}
                </span>
              </label>
              <p className="text-xs text-muted-foreground mb-3">Leave empty to show all active themes</p>
              <div className="max-h-40 overflow-y-auto space-y-1 rounded-xl border border-white/10 p-2">
                {themes.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No themes available</p>
                ) : (
                  themes.map(theme => (
                    <button
                      key={theme.theme_id}
                      onClick={() => toggleTheme(theme.theme_id)}
                      className={`w-full p-2 rounded-lg text-left text-sm transition-all flex items-center justify-between ${
                        formData.allowed_theme_ids.includes(theme.theme_id)
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <span>{theme.theme_name}</span>
                      {formData.allowed_theme_ids.includes(theme.theme_id) && (
                        <CheckCircle className="w-4 h-4" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
            
            {/* Packs Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Allowed Packs
                <span className="text-muted-foreground font-normal ml-2">
                  {formData.allowed_pack_ids.length === 0 ? '(All packs)' : `(${formData.allowed_pack_ids.length} selected)`}
                </span>
              </label>
              <p className="text-xs text-muted-foreground mb-3">Leave empty to show all published packs</p>
              <div className="max-h-40 overflow-y-auto space-y-1 rounded-xl border border-white/10 p-2">
                {packs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No published packs</p>
                ) : (
                  packs.map(pack => (
                    <button
                      key={pack.pack_id}
                      onClick={() => togglePack(pack.pack_id)}
                      className={`w-full p-2 rounded-lg text-left text-sm transition-all flex items-center justify-between ${
                        formData.allowed_pack_ids.includes(pack.pack_id)
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <span>{pack.title}</span>
                      {formData.allowed_pack_ids.includes(pack.pack_id) && (
                        <CheckCircle className="w-4 h-4" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
            
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Date (optional)</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={e => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">End Date (optional)</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={e => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            </div>
            
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl border border-white/10">
              <div>
                <p className="font-medium">Program Active</p>
                <p className="text-sm text-muted-foreground">Students can use this program code</p>
              </div>
              <button
                onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                className={`w-12 h-6 rounded-full transition-colors ${
                  formData.is_active ? 'bg-primary' : 'bg-white/20'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  formData.is_active ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={saveProgram} className="rounded-full bg-primary text-black">
              {editingProgram ? 'Save Changes' : 'Create Program'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Program?</DialogTitle>
            <DialogDescription>
              This will delete "{showDeleteConfirm?.program_name}". Sessions using this program will be preserved but will load the default configuration.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(null)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={() => deleteProgram(showDeleteConfirm?.program_id)}
              className="rounded-full bg-red-500 text-white hover:bg-red-600"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramManager;
